/**
 * The editor's Assets tab — the version editor's Assets tab, minus the library.
 *
 * Same chrome on purpose: one collapsible card per asset type, one card per
 * asset, a key you can rename in place, a use counter that names the components
 * bound to the key, and an Add button per type. Moving between the two editors
 * should not feel like moving between two products.
 *
 * What is gone is everything mongo-shaped. A snapshot's assets are the snapshot:
 * each entry in its `assets` map carries the bytes as a base64 data URL, so
 * there are no buckets, no company media records, no cascade when something is
 * replaced and no delete-from-library. Bytes come from one of two places — a
 * file on your computer, or the asset library the agent fills up — and both end
 * the same way: inline in the snapshot, stored nowhere else.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import {
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import GetAppIcon from "@material-ui/icons/GetApp";
import PublishIcon from "@material-ui/icons/Publish";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import InnerComponentIcon from "../../../images/gameConfig/Inner-component-icon.svg";
import { sendCommand } from "../../modules/playEngineBridge";
import {
  assetIdFor,
  collectAssetKeyUsages,
  genAssetKey,
  getAssetMapFromConfig,
  getPairDepConfig,
  isPairDepType,
  localFileToBase64,
  normalizeAssetKey,
  rekeyAssetInConfig,
  removeAssetFromPlayEngineConfig,
  rewriteAsJsonDataUrl,
  validateAssetKey,
} from "../../modules/playEngineAssetUtils";
import { SNAPSHOT_IFRAME_ID, guessSnapshotAssetsFolder, sourceFromLibraryEntry } from "../../modules/snapshotEditorApi";
import { getKeepUploads, keepUploadInLibrary, setKeepUploads } from "../../modules/snapshotCustomUploads";
import { optimizeSourceForSnapshot } from "../../modules/snapshotOptimize";
import { downloadSnapshotAssets } from "../../modules/snapshotAssetArchive";
import DialogAssetLibrary, { LIBRARY_ASSET_TYPES } from "./dialogAssetLibrary";
import PlayEngineTabSearch, { matchesTabSearch } from "../playEngineTabSearch";
import {
  BAR_BG,
  CONTROL_BG,
  CONTROL_BORDER,
  FIELD_LABEL,
  PANEL_BG,
  SECTION_DIVIDER,
} from "./panelPalette";

const ASSET_TYPES = ["image", "audio", "video", "font", "atlas", "bitmap-font"];

/** Cards read as raised off the panel; the divider draws their edge. */
const CARD_STYLE = {
  backgroundColor: BAR_BG,
  border: `1px solid ${SECTION_DIVIDER}`,
  boxShadow: "none",
};

/**
 * Outlined buttons, in the panel's own greys rather than the theme's. The
 * recessed fill plus a lighter edge is what makes them read as buttons: a
 * divider-coloured outline alone vanishes into the card behind it.
 */
const OUTLINED_BUTTON_STYLE = {
  color: FIELD_LABEL,
  backgroundColor: CONTROL_BG,
  border: `1px solid ${CONTROL_BORDER}`,
  textTransform: "none",
};

// A half of a pair that has been picked but not yet uploaded — the same green
// tick Version Settings uses, so the two upload flows read alike.
const STAGED_BUTTON_STYLE = {
  color: "#4caf50",
  borderColor: "#4caf50",
};

/** Dialogs are panel surfaces too — they open over this one. */
const DIALOG_PAPER_PROPS = { style: { backgroundColor: PANEL_BG } };

/** Rim of the row Version Settings just linked to. */
const HIGHLIGHT_BORDER = "#7e9bd6";

function EndIcon(props) {
  return <DisabledByDefaultRoundedIcon {...props} sx={{ opacity: 0.3 }} />;
}

function assetTypeItemId(type) {
  return `snapshot-assets-${type}`;
}

function getAcceptString(type) {
  switch (type) {
  case "image": return ".png,.jpg,.jpeg,.gif,.webp,.svg";
  case "audio": return ".mp3,.ogg,.wav,.m4a,.aac";
  case "video": return ".mp4,.webm";
  case "font": return ".ttf,.otf,.woff,.woff2";
  case "atlas": return ".json,.png,.jpg,.jpeg";
  case "bitmap-font": return ".xml,.png,.jpg,.jpeg";
  default: return "*";
  }
}

function componentUsageLabel(comp) {
  if (!comp?.id) return null;
  return comp.type ? `${comp.type} - (${comp.id})` : comp.id;
}

function canPickFromLibrary(type) {
  return LIBRARY_ASSET_TYPES.includes(type);
}

/** The folder a library entry's file lives in — "" for the library root. */
function libraryDirOf(relativePath) {
  if (!relativePath) return "";
  const splitAt = relativePath.lastIndexOf("/");
  return splitAt === -1 ? "" : relativePath.slice(0, splitAt);
}

/** The bytes an <img> can show. Fonts and pair metrics have nothing to preview. */
function thumbnailSource(entry) {
  const source = entry && entry.source;
  if (typeof source === "string" && source.startsWith("data:image/")) return source;
  if (source && typeof source === "object" && typeof source.image === "string") return source.image;
  return null;
}

function describeSize(entry) {
  const source = entry && entry.source;
  const text =
    typeof source === "string"
      ? source
      : source && typeof source === "object"
        ? `${source.image || ""}${source.xml || source.json || ""}`
        : "";
  if (!text) return "no source";
  // base64 carries 3 bytes per 4 characters; close enough to label a row with.
  const bytes = Math.round((text.length * 3) / 4);
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** Read local file(s) into the shape the asset map stores. */
async function sourceFromLocalFiles(type, files) {
  const list = Array.from(files || []);
  if (list.length === 0) return null;

  if (!isPairDepType(type)) return localFileToBase64(list[0]);

  const config = getPairDepConfig(type);
  const metaFile = list.find((file) => new RegExp(`\\.${config.metaKey}$`, "i").test(file.name));
  const imageFile = list.find((file) => file !== metaFile);
  if (!metaFile || !imageFile) {
    throw new Error(`Pick both halves of the ${type} (image + ${config.metaLabel}).`);
  }

  const [image, meta] = await Promise.all([
    localFileToBase64(imageFile),
    localFileToBase64(metaFile),
  ]);
  return {
    image,
    [config.metaKey]: config.metaKey === "json" ? rewriteAsJsonDataUrl(meta) : meta,
  };
}

export default function ContainerAssets({
  editor,
  userPermissions,
  createSnackbar,
  highlightAssetKey,
  onHighlightDone,
  onNavigateToAsset,
}) {
  const config = editor.snapshot;
  const assetsMap = useMemo(() => getAssetMapFromConfig(config), [config]);

  const [expandedItems, setExpandedItems] = useState([]);
  const [search, setSearch] = useState("");
  // The preference itself lives in a module so every upload path shares it; this
  // mirror exists only so the switch re-renders when it is flipped.
  const [keepUploads, setKeepUploadsState] = useState(getKeepUploads());
  const [busyKeys, setBusyKeys] = useState({});
  const [addType, setAddType] = useState(null);
  // Building the archive blocks the thread, so the button says so while it does.
  const [archiving, setArchiving] = useState(false);
  // { assetType, multiple, onChoose } — the library serves both the Add flow and
  // the per-row replace, so it is opened with what to do with the result.
  const [library, setLibrary] = useState(null);

  const canUpdate = _.get(userPermissions, "update") !== false;

  // Reference count + component labels for each asset key.
  const { usageByKey, usageLabelsByKey } = useMemo(() => {
    const counts = {};
    const labels = {};
    const addUsage = (assetKey, label) => {
      if (!assetKey || !label) return;
      counts[assetKey] = (counts[assetKey] || 0) + 1;
      if (!labels[assetKey]) labels[assetKey] = [];
      if (!labels[assetKey].includes(label)) labels[assetKey].push(label);
    };

    for (const comp of config?.components || []) {
      const label = componentUsageLabel(comp);
      for (const dep of comp.dependencies || []) {
        if (dep?.assetKey) addUsage(dep.assetKey, label);
      }
    }

    const compById = Object.fromEntries((config?.components || []).map((c) => [c.id, c]));
    const overrides = _.get(config, "localization.overrides") || {};
    for (const [language, langOverrides] of Object.entries(overrides)) {
      for (const [componentId, compOverride] of Object.entries(langOverrides || {})) {
        const label = componentUsageLabel(compById[componentId]) || componentId;
        for (const value of Object.values(compOverride?.dependencyOverrides || {})) {
          if (typeof value === "string") addUsage(value, `${label} [${language}]`);
        }
      }
    }

    return { usageByKey: counts, usageLabelsByKey: labels };
  }, [config]);

  const rowsByType = useMemo(() => {
    const grouped = Object.fromEntries(ASSET_TYPES.map((type) => [type, []]));
    Object.entries(assetsMap)
      .map(([assetKey, entry]) => ({ assetKey, ...entry }))
      .sort((a, b) => (a.assetKey || "").localeCompare(b.assetKey || ""))
      .forEach((row) => {
        if (grouped[row.type]) grouped[row.type].push(row);
      });
    return grouped;
  }, [assetsMap]);
  const matchingTypes = ASSET_TYPES.filter((type) =>
    rowsByType[type].some((row) => matchesTabSearch(row.assetKey, search))
  );

  /**
   * Jump to + highlight a row when Version Settings links to an asset key.
   *
   * The type's card is collapsed by default, so it has to be expanded before
   * the row exists to scroll to; the scroll then waits for the tab switch and
   * the expand animation to settle. Scrolling the tab panel rather than calling
   * scrollIntoView keeps the whole right-hand pane from being dragged around.
   */
  useEffect(() => {
    if (!highlightAssetKey || !assetsMap[highlightAssetKey]) return undefined;

    const assetType = assetsMap[highlightAssetKey].type;
    if (assetType) {
      const itemId = assetTypeItemId(assetType);
      setExpandedItems((previous) => (previous.includes(itemId) ? previous : [...previous, itemId]));
    }

    const scrollTimer = window.setTimeout(() => {
      const row = document.getElementById(`snapshot-asset-row-${highlightAssetKey}`);
      const panel = row?.closest("[role=\"tabpanel\"]:not([hidden])");
      if (row && panel) {
        const panelRect = panel.getBoundingClientRect();
        const rowRect = row.getBoundingClientRect();
        panel.scrollTop += rowRect.top - panelRect.top - panel.clientHeight / 2 + row.clientHeight / 2;
      }
    }, 350);

    const clearTimer = window.setTimeout(() => onHighlightDone?.(), 1000);

    return () => {
      window.clearTimeout(scrollTimer);
      window.clearTimeout(clearTimer);
    };
  }, [highlightAssetKey, assetsMap, onHighlightDone]);

  const totalVisibleAssets = useMemo(
    () => ASSET_TYPES.reduce((sum, type) => sum + (rowsByType[type]?.length || 0), 0),
    [rowsByType]
  );

  /**
   * Zip every asset the snapshot embeds — the list above plus the engine sound
   * slots, which are their own registry and appear nowhere in this tab.
   *
   * Built here from the open file rather than asked of the server: the bytes are
   * already in the browser, and an export would rebuild the playable to reach
   * the same ones.
   */
  async function downloadAllAssets() {
    if (archiving) return;
    setArchiving(true);
    try {
      // Base64 decoding and the archive itself are synchronous and can hold the
      // thread for a second on a heavy snapshot. Yield once so the button has
      // actually repainted as busy before that starts — otherwise the spinner
      // appears only after the work it was meant to cover.
      await new Promise((resolve) => setTimeout(resolve, 0));

      const { count, skipped } = downloadSnapshotAssets(editor.snapshot, editor.loadedFileName);
      if (count === 0) {
        createSnackbar?.("This snapshot has no downloadable asset bytes.", "warning", false);
        return;
      }
      createSnackbar?.(
        skipped.length
          ? `Downloading ${count} file(s) — ${skipped.length} slot(s) had no embedded bytes.`
          : `Downloading ${count} file(s).`,
        skipped.length ? "warning" : "success",
        false
      );
    } catch (err) {
      // A snapshot big enough to fail the archive's single allocation lands here
      // as a RangeError; everything else is a malformed source.
      console.error("downloadAllAssets failed:", err);
      createSnackbar?.(`Could not build the archive: ${err.message}`, "error", false);
    } finally {
      setArchiving(false);
    }
  }

  // ── Persistence + iframe helpers ───────────────────────────────────────────

  /**
   * Edit the snapshot. The hook diffs what changed and writes it to the file,
   * so this is the whole persistence story — there is no version document.
   */
  const persist = useCallback(
    (mutator) => {
      const next = _.cloneDeep(editor.snapshot);
      if (!next.assets || Array.isArray(next.assets)) next.assets = {};
      mutator(next);
      editor.applySnapshotChange(next);
      return next;
    },
    [editor]
  );

  function setBusy(assetKey, value) {
    setBusyKeys((previous) => ({ ...previous, [assetKey]: value }));
  }

  const notifyReadOnly = useCallback(() => {
    createSnackbar?.(
      "You do not have permission to update, contact your administrator.",
      "warning",
      false
    );
  }, [createSnackbar]);

  /** Push an asset's bytes to the iframe so every bound handle updates live. */
  const loadHandle = useCallback(async (type, assetKey, source) => {
    try {
      await sendCommand(
        SNAPSHOT_IFRAME_ID,
        { type: "playengine:set-asset", assetId: assetIdFor(type, assetKey), source },
        isPairDepType(type) ? 15000 : 5000
      );
    } catch (err) {
      // The file is already saved; a stale preview only needs a reload.
      console.warn(`[snapshotAssets] set-asset failed (snapshot saved): ${err.message}`);
    }
  }, []);

  /** Rewire iframe bindings after an asset key change. */
  const rewireAssetUsages = useCallback(async (usages, type, newKey) => {
    const handleKey = assetIdFor(type, newKey);
    const timeout = isPairDepType(type) ? 15000 : 5000;
    for (const usage of usages) {
      try {
        if (usage.kind === "dep") {
          await sendCommand(SNAPSHOT_IFRAME_ID, {
            type: "playengine:dispatch-action",
            action: {
              target: usage.componentId,
              name: "changeDependency",
              args: { key: usage.depKey, handleKey },
            },
          }, timeout);
        } else {
          await sendCommand(SNAPSHOT_IFRAME_ID, {
            type: "playengine:dispatch-action",
            action: {
              target: "engine",
              name: "addLocalizationOverrideDependency",
              args: {
                language: usage.language,
                componentId: usage.componentId,
                dependencyKey: usage.depKey,
                handleKey,
              },
            },
          }, timeout);
        }
      } catch (err) {
        console.warn(`[snapshotAssets] rebind failed for "${usage.componentId}": ${err.message}`);
      }
    }
  }, []);

  // ── Operations ─────────────────────────────────────────────────────────────

  const setAssetSource = useCallback(
    // `libraryPath` is the folder this source was just picked from in the
    // library — null (the default) means it was not, which is what a local
    // upload passes. It is what the row's own Library button reopens to next
    // time, so a source that no longer comes from the library must not leave
    // a stale folder behind.
    async (assetKey, type, source, { libraryPath = null } = {}) => {
      setBusy(assetKey, true);
      try {
        persist((next) => {
          const entry = next.assets[assetKey];
          if (!entry) return;
          entry.source = source;
          // An inlined asset has nothing to reference; agent-authored entries
          // look the same way.
          entry.sourceRef = null;
          if (libraryPath !== null) entry.libraryPath = libraryPath;
          else delete entry.libraryPath;
        });
        await loadHandle(type, assetKey, source);
      } finally {
        setBusy(assetKey, false);
      }
    },
    [loadHandle, persist]
  );

  const addAsset = useCallback(
    async (type, source, { libraryPath = null } = {}) => {
      const assetKey = genAssetKey(type, Object.keys(assetsMap));
      persist((next) => {
        next.assets[assetKey] = { type, bucket: "nobucket", source, sourceRef: null };
        if (libraryPath !== null) next.assets[assetKey].libraryPath = libraryPath;
      });
      await loadHandle(type, assetKey, source);
      return assetKey;
    },
    [assetsMap, loadHandle, persist]
  );

  async function rekeyAsset(oldKey, type, entry, newKeyRaw) {
    const newKey = normalizeAssetKey(newKeyRaw);
    if (!newKey || newKey === oldKey) return;

    const error = validateAssetKey(newKey, Object.keys(assetsMap), oldKey);
    if (error) {
      createSnackbar?.(error, "warning", false);
      return;
    }

    setBusy(oldKey, true);
    try {
      const usages = collectAssetKeyUsages(editor.snapshot, oldKey);
      // The bytes are right here in the snapshot — no round trip to the engine
      // or to a media record is needed to carry them over to the new key.
      const source = entry.source;

      persist((next) => {
        rekeyAssetInConfig(next, oldKey, newKey);
      });

      if (source) {
        await loadHandle(type, newKey, source);
        if (usages.length > 0) await rewireAssetUsages(usages, type, newKey);
        try {
          await sendCommand(SNAPSHOT_IFRAME_ID, {
            type: "playengine:delete-asset",
            assetKey: oldKey,
            assetType: type,
          }, isPairDepType(type) ? 15000 : 5000);
        } catch (err) {
          console.warn(`[snapshotAssets] delete-asset after rekey failed: ${err.message}`);
        }
      }

      createSnackbar?.(`Asset key updated to "${newKey}".`, "success", false);
      // The highlight tracks a key, and that key no longer exists.
      if (highlightAssetKey === oldKey) onNavigateToAsset?.(newKey);
    } catch (err) {
      console.error("rekeyAsset failed:", err);
      createSnackbar?.(`Failed to rename key: ${err.message}`, "error", false);
    } finally {
      setBusy(oldKey, false);
      setBusy(newKey, false);
    }
  }

  async function deleteAsset(assetKey, type) {
    const used = usageByKey[assetKey] || 0;
    if (used > 0) {
      const confirmed = window.confirm(
        `This asset is used by ${used} component dependency/dependencies. ` +
        "Deleting it will unbind them. Continue?"
      );
      if (!confirmed) return;
    }

    setBusy(assetKey, true);
    try {
      persist((next) => {
        removeAssetFromPlayEngineConfig(next, assetKey);
      });
      try {
        await sendCommand(SNAPSHOT_IFRAME_ID, {
          type: "playengine:delete-asset",
          assetKey,
          assetType: type,
        }, isPairDepType(type) ? 15000 : 5000);
      } catch (err) {
        console.warn(`[snapshotAssets] delete-asset failed (snapshot saved): ${err.message}`);
      }
    } finally {
      setBusy(assetKey, false);
    }
  }

  // ── Entry points from the row / add dialog ─────────────────────────────────

  async function replaceFromFiles(assetKey, type, files) {
    if (!canUpdate) return notifyReadOnly();
    try {
      const source = await sourceFromLocalFiles(type, files);
      if (!source) return;
      const fileName = Array.from(files || [])[0]?.name;
      // The library keeps the file as the user picked it; only the copy going
      // into the snapshot is optimized.
      await setAssetSource(assetKey, type, await optimizeForSnapshot(type, source, fileName));
      void keepUploadInLibrary({
        baseID: editor.baseID,
        assetType: type,
        fileName,
        source,
        createSnackbar,
      });
    } catch (err) {
      console.error("replaceFromFiles failed:", err);
      createSnackbar?.(err.message, "warning", false);
    }
  }

  /** Optimize an asset's bytes on their way into the snapshot, and say so. */
  function optimizeForSnapshot(type, source, fileName) {
    return optimizeSourceForSnapshot({
      baseID: editor.baseID,
      assetType: type,
      source,
      fileName,
      onSaved: (stats) =>
        createSnackbar?.(`Image optimized by ${stats.savedPercent}% for this snapshot.`, "success", false),
    });
  }

  async function replaceFromLibrary(assetKey, type) {
    if (!canUpdate) return notifyReadOnly();
    // Priority: this asset's own remembered folder (most specific — it is
    // exactly where the CURRENT bytes came from) outranks the snapshot-wide
    // guess, which outranks the library's last-visited folder (undefined
    // falls through to that, in DialogAssetLibrary). `libraryPath` is already
    // a folder, never a file path, so a defined one is passed through as-is.
    const ownPath = assetsMap[assetKey]?.libraryPath;
    const initialDirPath = ownPath !== undefined
      ? ownPath
      : await guessSnapshotAssetsFolder(editor.baseID, config, editor.loadedFileName);
    setLibrary({
      assetType: type,
      multiple: false,
      initialDirPath,
      onChoose: async (entries) => {
        const picked = entries[0];
        const source = await sourceFromLibraryEntry(picked);
        // The library file itself is never rewritten — this compresses the copy
        // being embedded, exactly as a local upload is.
        await setAssetSource(assetKey, type, await optimizeForSnapshot(type, source, picked?.name), {
          libraryPath: libraryDirOf(picked?.relativePath),
        });
      },
    });
  }

  async function addFromLibrary(type) {
    const initialDirPath = await guessSnapshotAssetsFolder(editor.baseID, config, editor.loadedFileName);
    setLibrary({
      assetType: type,
      multiple: true,
      initialDirPath,
      onChoose: async (entries) => {
        for (const entry of entries) {
          // Sequential on purpose: each add reads the asset map to pick a free
          // key, and a parallel batch would hand out the same one twice.
          const source = await sourceFromLibraryEntry(entry);
          await addAsset(type, await optimizeForSnapshot(type, source, entry?.name), {
            libraryPath: libraryDirOf(entry?.relativePath),
          });
        }
        createSnackbar?.(`Added ${entries.length} ${type} asset(s).`, "success", false);
        setAddType(null);
      },
    });
  }

  if (!editor.snapshot) {
    return (
      <Typography variant="body2" style={{ color: "#8a8d93", padding: "16px 8px" }}>
        Select a snapshot to see its assets.
      </Typography>
    );
  }

  return (
    <div style={{ padding: "8px 4px", backgroundColor: PANEL_BG, minHeight: "100%" }}>
      <PlayEngineTabSearch value={search} onChange={setSearch} />
      {totalVisibleAssets === 0 && !search.trim() && (
        <Typography variant="body2" style={{ color: "#8a8d93", marginBottom: 8 }}>
          This snapshot has no assets. Expand a type below to add one, or pick media on a component
          in Version Settings.
        </Typography>
      )}
      {search.trim() && matchingTypes.length === 0 && (
        <Typography variant="body2" style={{ color: "#8a8d93", padding: 8 }}>
          No asset keys match this search.
        </Typography>
      )}

      <SimpleTreeView
        aria-label="snapshot-assets"
        expandedItems={search.trim() ? Array.from(new Set([...expandedItems, ...matchingTypes.map(assetTypeItemId)])) : expandedItems}
        onExpandedItemsChange={(event, itemIds) => setExpandedItems(itemIds)}
        slots={{
          expandIcon: ArrowRightIcon,
          collapseIcon: ArrowDropDownIcon,
          endIcon: EndIcon,
        }}
        sx={{ overflowX: "hidden", flexGrow: 1 }}
      >
        {ASSET_TYPES.map((type) => {
          const typeRows = (rowsByType[type] || []).filter((row) => matchesTabSearch(row.assetKey, search));
          if (search.trim() && typeRows.length === 0) return null;
          return (
            <Card key={type} style={{ margin: "5px", ...CARD_STYLE }}>
              <TreeItem
                itemId={assetTypeItemId(type)}
                label={(
                  <div style={{ display: "flex", alignItems: "center", minHeight: "32px", fontWeight: "bold" }}>
                    <img
                      src={InnerComponentIcon}
                      alt=""
                      style={{ width: "18px", height: "18px", marginRight: "8px" }}
                    />
                    <span style={{ color: "#eee", textTransform: "capitalize" }}>{type}</span>
                    <Chip
                      label={typeRows.length}
                      size="small"
                      style={{ marginLeft: 8, backgroundColor: PANEL_BG, color: FIELD_LABEL, height: 20 }}
                    />
                  </div>
                )}
              >
                <Card
                  style={{
                    backgroundColor: PANEL_BG,
                    boxShadow: "none",
                    borderTop: `1px solid ${SECTION_DIVIDER}`,
                    borderRadius: 0,
                  }}
                >
                  <CardContent style={{ paddingTop: 8, paddingBottom: 12 }}>
                    {typeRows.length === 0 ? (
                      <Typography variant="body2" style={{ color: "#8a8d93", marginBottom: 8 }}>
                        No {type} assets yet.
                      </Typography>
                    ) : (
                      typeRows.map((row) => (
                        <AssetRow
                          key={row.assetKey}
                          row={row}
                          usage={usageByKey[row.assetKey] || 0}
                          usageLabels={usageLabelsByKey[row.assetKey] || []}
                          busy={!!busyKeys[row.assetKey]}
                          highlighted={highlightAssetKey === row.assetKey}
                          canUpdate={canUpdate}
                          existingKeys={Object.keys(assetsMap)}
                          onRekey={rekeyAsset}
                          onDelete={deleteAsset}
                          onReplaceFromFiles={replaceFromFiles}
                          onReplaceFromLibrary={replaceFromLibrary}
                          onReadOnly={notifyReadOnly}
                        />
                      ))
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => (canUpdate ? setAddType(type) : notifyReadOnly())}
                      style={{ marginTop: 4, ...OUTLINED_BUTTON_STYLE }}
                    >
                      Add {type} asset
                    </Button>
                  </CardContent>
                </Card>
              </TreeItem>
            </Card>
          );
        })}
      </SimpleTreeView>

      {/* Under the list, because it acts on all of it. No permission gate: it
          only reads, and whoever may open the snapshot may keep its bytes. */}
      <div style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
        <Tooltip title="Zip every asset embedded in this snapshot, engine sounds included">
          <span>
            <Button
              variant="outlined"
              size="small"
              disabled={archiving}
              startIcon={
                archiving ? <CircularProgress size={14} style={{ color: FIELD_LABEL }} /> : <GetAppIcon />
              }
              onClick={downloadAllAssets}
              style={OUTLINED_BUTTON_STYLE}
            >
              {archiving ? "Preparing archive…" : "Download all assets"}
            </Button>
          </span>
        </Tooltip>
      </div>

      {/* One switch for every upload path — this tab's Add and Replace, and
          Version Settings' per-dependency uploads all consult it. An upload
          always embeds its bytes in the snapshot; this decides whether the file
          is ALSO kept in the library so the next snapshot can pick it. Sits at
          the foot of the tab: it is a setting, not something to step over on the
          way to the assets. */}
      <div style={{ borderTop: `1px solid ${SECTION_DIVIDER}`, marginTop: 8, paddingTop: 4 }}>
        <Tooltip title="Also write files you upload into the library's customUploads/ folder, so other snapshots can use them">
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="primary"
                checked={keepUploads}
                onChange={(event) => {
                  setKeepUploads(event.target.checked);
                  setKeepUploadsState(event.target.checked);
                }}
              />
            }
            label={
              <span style={{ fontSize: "12px", color: FIELD_LABEL }}>Keep uploads in the library</span>
            }
            style={{ marginLeft: 2 }}
          />
        </Tooltip>
      </div>

      {addType && (
        <AddAssetDialog
          type={addType}
          createSnackbar={createSnackbar}
          onPickFromLibrary={() => addFromLibrary(addType)}
          onUpload={async (files) => {
            const source = await sourceFromLocalFiles(addType, files);
            if (!source) return;
            const fileName = Array.from(files || [])[0]?.name;
            await addAsset(addType, await optimizeForSnapshot(addType, source, fileName));
            createSnackbar?.(`Added ${addType} asset.`, "success", false);
            void keepUploadInLibrary({
              baseID: editor.baseID,
              assetType: addType,
              fileName,
              source,
              createSnackbar,
            });
          }}
          onClose={() => setAddType(null)}
        />
      )}

      {library && (
        <DialogAssetLibrary
          baseID={editor.baseID}
          assetType={library.assetType}
          multiple={library.multiple}
          initialDirPath={library.initialDirPath}
          canWrite={canUpdate}
          createSnackbar={createSnackbar}
          onChoose={library.onChoose}
          onClose={() => setLibrary(null)}
        />
      )}
    </div>
  );
}

/** A single editable asset row. */
function AssetRow({
  row,
  usage,
  usageLabels,
  busy,
  highlighted,
  canUpdate,
  existingKeys,
  onRekey,
  onDelete,
  onReplaceFromFiles,
  onReplaceFromLibrary,
  onReadOnly,
}) {
  const { assetKey, type } = row;
  const [keyDraft, setKeyDraft] = useState(assetKey);
  const [keyError, setKeyError] = useState("");

  useEffect(() => {
    setKeyDraft(assetKey);
    setKeyError("");
  }, [assetKey]);

  const inputId = `snapshot-asset-upload-${assetKey}`;
  const thumb = thumbnailSource(row);

  // Bitmap fonts and atlases are two files, and asking for both in one
  // multi-select left people guessing which half was missing. Stage them the
  // way Version Settings does instead: one button per half, each ticking green
  // as it is picked, and an apply that only lights up once both are in hand.
  const isPair = isPairDepType(type);
  const pairConfig = isPair ? getPairDepConfig(type) : null;
  const [staged, setStaged] = useState({ imageFile: null, metaFile: null });
  const pairReady = Boolean(staged.imageFile && staged.metaFile);

  // A different asset in this row means the half-picked pair is not ours.
  useEffect(() => {
    setStaged({ imageFile: null, metaFile: null });
  }, [assetKey]);

  function commitKeyChange() {
    const next = normalizeAssetKey(keyDraft);
    setKeyDraft(next);
    if (next === assetKey) {
      setKeyError("");
      return;
    }
    const error = validateAssetKey(next, existingKeys, assetKey);
    if (error) {
      setKeyError(error);
      return;
    }
    setKeyError("");
    onRekey(assetKey, type, row, next);
  }

  return (
    <Card
      id={`snapshot-asset-row-${assetKey}`}
      style={{
        marginBottom: 8,
        ...CARD_STYLE,
        // Same "you were sent here" treatment as the version editor's Assets
        // tab, fading back to the card's own colours when the link expires.
        ...(highlighted
          ? {
            backgroundColor: "#3d4a5c",
            border: `1px solid ${HIGHLIGHT_BORDER}`,
            boxShadow: "0 0 16px rgba(126, 155, 214, 0.45)",
          }
          : {}),
        transition: "background-color 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease",
      }}
    >
      <CardContent style={{ paddingBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 4 }}>
          <Tooltip
            title={
              usageLabels.length > 0 ? (
                <div>
                  {usageLabels.map((label) => (
                    <div key={label}>{label}</div>
                  ))}
                </div>
              ) : "Not used by any component"
            }
          >
            <Chip
              label={`${usage} use${usage === 1 ? "" : "s"}`}
              size="small"
              style={{ backgroundColor: PANEL_BG, color: usage ? "#cfcf8a" : "#8a8d93", marginRight: 4 }}
            />
          </Tooltip>
          <Typography variant="caption" style={{ color: "#8a8d93" }}>
            {describeSize(row)}
          </Typography>
          <div style={{ flex: 1 }} />
          {busy && <CircularProgress size={18} />}
          <Tooltip title="Delete asset">
            <span>
              <IconButton
                size="small"
                disabled={busy || !canUpdate}
                onClick={() => (canUpdate ? onDelete(assetKey, type) : onReadOnly())}
              >
                <DeleteIcon fontSize="small" style={{ color: "#d98a8a" }} />
              </IconButton>
            </span>
          </Tooltip>
        </div>

        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <TextField
            label="Key"
            value={keyDraft}
            disabled={busy || !canUpdate}
            error={!!keyError}
            helperText={keyError || undefined}
            onChange={(e) => {
              setKeyDraft(e.target.value);
              if (keyError) setKeyError("");
            }}
            onBlur={commitKeyChange}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitKeyChange();
              }
            }}
            InputProps={{ style: { color: "#eee", fontFamily: "monospace" } }}
            InputLabelProps={{ style: { color: FIELD_LABEL } }}
            style={{ minWidth: 180 }}
          />

          <div style={{ flex: 1, minWidth: 200 }}>
            <Typography variant="caption" style={{ color: FIELD_LABEL, display: "block" }}>Source</Typography>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                  borderRadius: 3,
                  backgroundColor: PANEL_BG,
                  border: `1px solid ${SECTION_DIVIDER}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                {thumb ? (
                  <img src={thumb} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                ) : (
                  <Typography variant="caption" style={{ color: "#8a8d93", fontSize: 10 }}>
                    {row.source ? type : "empty"}
                  </Typography>
                )}
              </div>

              {isPair ? (
                <>
                  <input
                    id={`${inputId}-img`}
                    type="file"
                    hidden
                    accept=".png,.jpg,.jpeg"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) setStaged((current) => ({ ...current, imageFile: file }));
                      event.target.value = "";
                    }}
                  />
                  <input
                    id={`${inputId}-meta`}
                    type="file"
                    hidden
                    accept={pairConfig.metaAccept}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) setStaged((current) => ({ ...current, metaFile: file }));
                      event.target.value = "";
                    }}
                  />

                  <Tooltip title={`The ${type} image`}>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={busy}
                        onClick={() => {
                          if (!canUpdate) return onReadOnly();
                          document.getElementById(`${inputId}-img`)?.click();
                        }}
                        style={{
                          ...OUTLINED_BUTTON_STYLE,
                          ...(staged.imageFile ? STAGED_BUTTON_STYLE : null),
                        }}
                      >
                        {staged.imageFile ? "✓ IMG" : "+ IMG"}
                      </Button>
                    </span>
                  </Tooltip>

                  <Tooltip title={`The ${type} ${pairConfig.metaLabel} file`}>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={busy}
                        onClick={() => {
                          if (!canUpdate) return onReadOnly();
                          document.getElementById(`${inputId}-meta`)?.click();
                        }}
                        style={{
                          ...OUTLINED_BUTTON_STYLE,
                          ...(staged.metaFile ? STAGED_BUTTON_STYLE : null),
                        }}
                      >
                        {staged.metaFile ? `✓ ${pairConfig.metaLabel}` : `+ ${pairConfig.metaLabel}`}
                      </Button>
                    </span>
                  </Tooltip>

                  <Tooltip
                    title={
                      pairReady
                        ? `Replace this ${type}`
                        : `Pick both the image and the ${pairConfig.metaLabel} first`
                    }
                  >
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={busy || !pairReady}
                        startIcon={<PublishIcon fontSize="small" />}
                        onClick={() => {
                          if (!canUpdate) return onReadOnly();
                          onReplaceFromFiles(assetKey, type, [staged.imageFile, staged.metaFile]);
                          setStaged({ imageFile: null, metaFile: null });
                        }}
                        style={OUTLINED_BUTTON_STYLE}
                      >
                        Upload
                      </Button>
                    </span>
                  </Tooltip>
                </>
              ) : (
                <>
                  <input
                    id={inputId}
                    type="file"
                    hidden
                    accept={getAcceptString(type)}
                    onChange={(event) => {
                      onReplaceFromFiles(assetKey, type, event.target.files);
                      event.target.value = "";
                    }}
                  />
                  <Tooltip title="Replace with a file from your computer">
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PublishIcon fontSize="small" />}
                        disabled={busy}
                        onClick={() => {
                          if (!canUpdate) return onReadOnly();
                          document.getElementById(inputId)?.click();
                        }}
                        style={OUTLINED_BUTTON_STYLE}
                      >
                        Upload
                      </Button>
                    </span>
                  </Tooltip>
                </>
              )}
              {canPickFromLibrary(type) && (
                <Tooltip title="Replace from the asset library">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PhotoLibraryIcon fontSize="small" />}
                      disabled={busy}
                      onClick={() => onReplaceFromLibrary(assetKey, type)}
                      style={OUTLINED_BUTTON_STYLE}
                    >
                      Library
                    </Button>
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** Add-asset dialog: pick from the library, or upload from your computer. */
function AddAssetDialog({ type, createSnackbar, onPickFromLibrary, onUpload, onClose }) {
  const [staged, setStaged] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const pairConfig = isPairDepType(type) ? getPairDepConfig(type) : null;
  const ready = pairConfig ? Boolean(staged.imageFile && staged.metaFile) : Boolean(staged.file);

  async function handleUpload() {
    setSubmitting(true);
    try {
      await onUpload(pairConfig ? [staged.imageFile, staged.metaFile] : [staged.file]);
      onClose();
    } catch (err) {
      console.error("Add asset upload failed:", err);
      createSnackbar?.(`Could not add the asset: ${err.message}`, "error", false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={DIALOG_PAPER_PROPS}
    >
      <DialogTitle>Add {type} asset</DialogTitle>
      <DialogContent>
        {canPickFromLibrary(type) ? (
          <>
            <Typography variant="subtitle2" style={{ marginBottom: 6, color: FIELD_LABEL }}>
              Select from library
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<PhotoLibraryIcon />}
              disabled={submitting}
              onClick={onPickFromLibrary}
              style={OUTLINED_BUTTON_STYLE}
            >
              Browse asset library
            </Button>
          </>
        ) : (
          <Typography variant="caption" style={{ color: "#8a8d93" }}>
            The asset library has no “{type}” files — upload one from your computer.
          </Typography>
        )}

        <Typography variant="subtitle2" style={{ margin: "16px 0 6px", color: FIELD_LABEL }}>
          Or upload new
        </Typography>
        {pairConfig ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Button variant="outlined" component="label" disabled={submitting} style={OUTLINED_BUTTON_STYLE}>
              {staged.imageFile ? `Image: ${staged.imageFile.name}` : "Choose image"}
              <input
                type="file"
                hidden
                accept=".png,.jpg,.jpeg"
                onChange={(e) => setStaged({ ...staged, imageFile: e.target.files?.[0] })}
              />
            </Button>
            <Button variant="outlined" component="label" disabled={submitting} style={OUTLINED_BUTTON_STYLE}>
              {staged.metaFile
                ? `${pairConfig.metaLabel}: ${staged.metaFile.name}`
                : `Choose ${pairConfig.metaLabel}`}
              <input
                type="file"
                hidden
                accept={pairConfig.metaAccept}
                onChange={(e) => setStaged({ ...staged, metaFile: e.target.files?.[0] })}
              />
            </Button>
          </div>
        ) : (
          <Button
            variant="outlined"
            component="label"
            fullWidth
            disabled={submitting}
            style={OUTLINED_BUTTON_STYLE}
          >
            {staged.file ? staged.file.name : "Choose file"}
            <input
              type="file"
              hidden
              accept={getAcceptString(type)}
              onChange={(e) => setStaged({ ...staged, file: e.target.files?.[0] })}
            />
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          fullWidth
          disabled={submitting || !ready}
          onClick={handleUpload}
          style={{ marginTop: 8 }}
        >
          Add
        </Button>

        {submitting && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
            <CircularProgress size={18} /> <Typography variant="caption">Working…</Typography>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
